import React, { useMemo } from "react";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { useGetDialerQueueCountsQuery, useGetDialerSessionQuery } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SessionSummaryDialogProps {
  open: boolean;
  onClose: () => void;
}

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export function SessionSummaryDialog({ open, onClose }: SessionSummaryDialogProps) {
  const { calls, sessionId, campaign } = usePowerDialer();
  const { data: session } = useGetDialerSessionQuery(sessionId ?? "", { skip: !sessionId || !open });
  const { data: queueCounts } = useGetDialerQueueCountsQuery(campaign?.id ?? "", { skip: !campaign?.id || !open });

  const failedCount = useMemo(
    () => calls.filter((call) => ["FAILED", "NO_ANSWER", "BUSY", "CANCELED"].includes(call.status)).length,
    [calls],
  );

  const talkSeconds = useMemo(
    () => calls.filter((call) => call.connectedAt).reduce((sum, call) => sum + call.duration, 0),
    [calls],
  );

  const connectedCount = useMemo(() => calls.filter((call) => call.connectedAt).length, [calls]);

  const dispositionBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    calls.forEach((call) => {
      if (call.disposition) {
        counts[call.disposition] = (counts[call.disposition] ?? 0) + 1;
      }
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [calls]);

  const summaryRows = [
    { label: "Leads dialed", value: `${session?.dials ?? 0} of ${queueCounts?.total ?? 0}` },
    { label: "Answered", value: String(session?.liveAnswers ?? 0) },
    { label: "Connects", value: String(session?.connects ?? connectedCount) },
    { label: "Conversations", value: String(session?.conversations ?? 0) },
    { label: "Talk time", value: formatDuration(session?.connectedSeconds ?? talkSeconds) },
    { label: "Wrap up time", value: formatDuration(session?.wrapUpSeconds ?? 0) },
    { label: "Break time", value: formatDuration(session?.breakSeconds ?? 0) },
    { label: "Voicemails", value: String(session?.voicemails ?? 0) },
    { label: "No answer / busy / failed", value: String(failedCount) },
    { label: "Abandoned", value: String(session?.abandons ?? 0) },
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Session summary</DialogTitle>
          <DialogDescription>Here's how this dialing session went.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          {summaryRows.map((row) => (
            <div key={row.label} className="flex items-center justify-between py-1.5 border-b last:border-0">
              <span className="text-sm text-muted-foreground">{row.label}</span>
              <span className="text-sm font-semibold dark:text-white">{row.value}</span>
            </div>
          ))}
        </div>

        {dispositionBreakdown.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Dispositions
            </h4>
            <div className="space-y-1">
              {dispositionBreakdown.map(([disposition, count]) => (
                <div key={disposition} className="flex items-center justify-between py-1">
                  <span className="text-sm">{disposition}</span>
                  <span className="text-sm font-semibold dark:text-white">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
